<script>
import OmniDialog from "@/components/OmniDialog.vue";
import DatePicker from "@/components/pickers/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import FileUploadingGrid from "@/components/FileUploadingGrid.vue";
import ValidationMixin from "@/mixins/validation.mixin";
import BaseView from "@/views/BaseView.vue";
import { useDateTime } from "@/composables/index.ts";
import { mapActions } from "pinia";
import { useSnackbarStore } from "@/store/snackbar.store.js";
import { useError } from "@/composables/useError.js";
import TimezoneSelect from "@/components/inputs/TimezoneSelect.vue";
import FreightDetails from "@/views/trips/view/components/FreightDetails.vue";
import { OrderStopType } from "@/data/order.ts";

const { dateToUTC, timezonesShortList } = useDateTime();
const { showAPIError, scrollToError } = useError();

export default {
  name: "DeliveryInfoDialog",
  components: { FreightDetails, TimezoneSelect, FileUploadingGrid, TimePicker, DatePicker, OmniDialog },
  mixins: [ValidationMixin, BaseView],
  props: {
    title: String,
    modelValue: Boolean,
    preload: Boolean,
    readonly: Boolean,
    tripId: Number,
    route: Object,
    stop: Object,
  },
  emits: ["update:modelValue", "save"],
  data() {
    return {
      loading: false,
      unloadData: {
        date: null,
        time: null,
        timezone: null,
        signedBy: null,
        podFiles: [],
        files: [],
        freights: [],
      },
    };
  },
  computed: {
    freights() {
      return this.route.freights;
    },
    stops() {
      return this.route.routeStops;
    },
    pickUps() {
      return this.stops.filter((stop) => stop.type === OrderStopType.pickup);
    },
    stopFreights() {
      return this.freights.filter((freight) => this.stop.freights.includes(freight.id));
    },
    showDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    timezonesShortListComputed() {
      return timezonesShortList;
    },
  },
  watch: {
    showDialog: {
      async handler(value) {
        if (!value) return;

        this.unloadData.freights = [...this.stopFreights];

        if (this.preload) {
          this.unloadData.signedBy = this.stop.podSignedBy;
          this.unloadData.timezone = this.stop.timezone;
          const date = this.$dayjs(this.stop.loadUnloadTime).tz(this.stop.timezone);
          this.unloadData.date = date.format("YYYY-MM-DD");
          this.unloadData.time = date.format("HH:mm");

          const filesResp = await this.$api.trips.getTripFiles(this.tripId);
          if (filesResp.success) {
            const stopFiles = filesResp.data
              .filter((f) => f.tags.includes("route_stop:" + this.stop.id))
              .map((f) => ({
                ...f,
                previewPath: this.getPreviewPath(f),
              }));
            this.unloadData.podFiles = stopFiles.filter((f) => f.tags.includes("pod"));
            this.unloadData.files = stopFiles.filter((f) => f.tags.includes("other"));
          }
        } else {
          this.unloadData.timezone = this.stop.facility.timezone;
          const date = this.$dayjs().tz(this.unloadData.timezone);
          this.unloadData.date = date.format("YYYY-MM-DD");
          this.unloadData.time = date.format("HH:mm");
        }
      },
      immediate: true,
    },
    async modelValue(oldUser, newUser) {
      // load driver-specific data
      if (!oldUser.isDriver && newUser.isDriver) {
        const driverResponse = await this.$api.users.findDriverById(newUser.id);
        if (driverResponse.success) {
          this.user = { ...this.user, ...driverResponse.data };
        }
      }

      if (this.modelValue.isDriver && this.driverInTruck === null) {
        const resp = await this.$api.trucks.findTrucks({ driver: this.user.id });
        if (resp.success) {
          this.driverInTruck = resp.data.total > 0 ? resp.data.items[0] : false;
        }
      }
    },
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarError"]),
    async save() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        scrollToError();
        return;
      }
      this.loading = true;

      const podUploads = this.unloadData.podFiles.map(async (uploadingFile) => {
        uploadingFile.loading = true;
        try {
          const fileResponse = await this.$api.trips.uploadRouteStopFile(
            this.tripId,
            this.stop.id,
            "pod",
            uploadingFile.file,
            "dummy",
          );
          if (fileResponse.success) {
            Object.assign(uploadingFile, {
              ...fileResponse.data,
              previewPath: this.getPreviewPath(fileResponse.data),
            });
          } else {
            uploadingFile.error = true;
          }
        } catch (e) {
          uploadingFile.error = true;
        } finally {
          uploadingFile.loading = false;
        }
      });

      const otherUploads = this.unloadData.files.map(async (uploadingFile) => {
        uploadingFile.loading = true;
        try {
          const fileResponse = await this.$api.trips.uploadRouteStopFile(
            this.tripId,
            this.stop.id,
            "other",
            uploadingFile.file,
            "dummy",
          );
          if (fileResponse.success) {
            Object.assign(uploadingFile, fileResponse.data);
          } else {
            uploadingFile.error = true;
          }
        } catch (e) {
          uploadingFile.error = true;
        } finally {
          uploadingFile.loading = false;
        }
      });

      await Promise.all([...podUploads, ...otherUploads]);
      const response = await this.$api.trips.unloadRouteStop(this.tripId, this.stop.id, {
        unloadTime: dateToUTC(this.unloadData.date + " " + this.unloadData.time, this.unloadData.timezone),
        podFiles: this.unloadData.podFiles,
        otherFiles: this.unloadData.files,
        podSignedBy: this.unloadData.signedBy,
        freights: this.unloadData.freights,
      });
      if (response.success) {
        this.$emit("save");
        this.showDialog = false;
      } else {
        showAPIError(response, "Error unload verification at stop");
      }
      this.loading = false;
    },
  },
};
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :disabled="loading"
    :loading="loading"
    :primary-action-label="readonly ? null : 'Save'"
    :title="title"
    size="large"
    @primary-action:click="save"
  >
    <v-form ref="form">
      <div class="mb-6">
        <div class="font-size-16 font-weight-500 mb-6">POD info:</div>
        <div class="text-grey-darken-1">Freight(s):</div>

        <freight-details
          v-for="(item, index) in pickUps"
          :key="item.id"
          v-model="unloadData.freights"
          :disabled="readonly"
          :index="index"
          :pick-up="item"
          :route-stops="route.routeStops"
        />

        <v-text-field
          v-model="unloadData.signedBy"
          :disabled="readonly"
          :rules="[requiredValidator]"
          class="required mt-6"
          color="primary"
          data-qa="signed-by-field"
          density="compact"
          hide-details="auto"
          label="Signed by"
          variant="outlined"
        />
      </div>
      <div class="mb-6">
        <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Attached files</div>
        <div class="text-grey-darken-1 font-weight-500 mb-4">POD files<span class="text-error">*</span></div>
        <file-uploading-grid
          v-model="unloadData.podFiles"
          :columns="3"
          :disabled="readonly"
          required
        ></file-uploading-grid>
        <div class="text-grey-darken-1 font-weight-500 mb-4 mt-6">Other files</div>
        <file-uploading-grid v-model="unloadData.files" :columns="3" :disabled="readonly"></file-uploading-grid>
      </div>
      <div>
        <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Time</div>
        <v-row>
          <v-col cols="3">
            <timezone-select
              v-model="unloadData.timezone"
              :disabled="readonly"
              :items="timezonesShortListComputed"
              :rules="[requiredValidator]"
              required
            />
          </v-col>
          <v-col cols="6">
            <date-picker v-model="unloadData.date" :disabled="readonly" label="Date" required></date-picker>
          </v-col>
          <v-col cols="3">
            <time-picker v-model="unloadData.time" :disabled="readonly" label="Time" required></time-picker>
          </v-col>
        </v-row>
      </div>
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped></style>
